import apiClient from '../client';
import ENDPOINTS from '../endpoints';

/* Email templates. A template carries its body copy AND the definition of the
   form that fills it, so adding a template is a data change — the compose page
   renders whatever fields come back rather than hard-coding one form.

   Body copy carries {{key}} placeholders matching each field's `key`.
   A template saved with no networkId is shared with every class; the list
   endpoint returns this network's templates plus those shared ones. */

function normalizeField(f) {
  return {
    key: f?.key || '',
    label: f?.label || f?.key || '',
    /* text | email | date | textarea | select | member */
    type: f?.type || 'text',
    placeholder: f?.placeholder || '',
    helpText: f?.helpText || '',
    isRequired: f?.isRequired === true,
    options: Array.isArray(f?.options) ? f.options : [],
  };
}

function normalizeTemplate(t) {
  return {
    id: t?.templateId || t?._id || '',
    templateId: t?.templateId || '',
    networkId: t?.networkId || '',
    name: t?.name || '',
    description: t?.description || '',
    subject: t?.subject || '',
    body: t?.body || '',
    fields: (t?.fields || []).map(normalizeField),
    status: t?.status || 'active',
  };
}

const emailTemplatesService = {
  /* The list omits nothing, but only the detail call is guaranteed to carry the
     full field set — the compose form fetches detail before rendering. */
  list: async (networkId, { page = 1, limit = 20, search = '', status = 'active' } = {}) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.EMAIL_TEMPLATES_BY_NETWORK(networkId), {
      params: { page, limit, search, ...(status ? { status } : {}) },
    });
    const data = res?.data ?? res;
    const rawList = data?.templates || data?.docs || data?.items || (Array.isArray(data) ? data : []);
    const total = Number(data?.totalCount ?? data?.total ?? rawList.length);
    const totalPages = Number(data?.totalPages ?? Math.max(1, Math.ceil(total / limit)));
    return { templates: rawList.map(normalizeTemplate), total, totalPages };
  },

  get: async (templateId) => {
    const res = await apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.EMAIL_TEMPLATE_BY_ID(templateId));
    const data = res?.data ?? res;
    return normalizeTemplate(data?.data || data?.template || data);
  },

  /* The body goes out as `templateBody` — `body` is the request envelope. Pass
     no networkId to share the template with every class. */
  create: async ({ networkId, name, description = '', subject = '', body = '', fields = [] }) => {
    const res = await apiClient.post(ENDPOINTS.ROOM_PARENT_ADMIN.EMAIL_TEMPLATE, {
      ...(networkId ? { networkId } : {}),
      name,
      description,
      subject,
      templateBody: body,
      fields,
    });
    const data = res?.data ?? res;
    return normalizeTemplate(data?.data || data);
  },
};

export default emailTemplatesService;
